import { typedSupabase } from "../lib/typed-supabase";
import type { IService, ServiceResponse } from "./service-registry";
import { logger } from "../lib/logger";
import { activityNotificationBridge } from "./activity-notification-bridge.service";
export interface TaskActivity {
    id: string;
    task_id: string;
    user_id: string;
    username: string | undefined;
    full_name: string | undefined;
    avatar_url: string | undefined;
    action_type: string;
    field_name: string | undefined;
    old_value: string | undefined;
    new_value: string | undefined;
    details: Record<string, unknown> | undefined;
    created_at: string;
    principal_id?: string | null;
    principal_name?: string | null;
    principal_kind?: string | null;
}
export interface LogTaskActivityParams {
    taskId: string;
    actionType: string;
    fieldName?: string;
    oldValue?: string;
    newValue?: string;
    details?: Record<string, unknown>;
    targetUserId?: string; // Who should receive the notification (if different from actor)
    skipNotification?: boolean; // Explicitly skip notification creation
}
export interface ExportActivitiesParams {
    taskIds?: string[];
    startDate?: string;
    endDate?: string;
    actionTypes?: string[];
    userIds?: string[];
    format: 'json' | 'csv';
}
export interface ExportActivitiesResult {
    data: string;
    filename: string;
    mimeType: string;
}
// ============================================================================
// TASK ACTIVITY SERVICE - Phase 4 Architecture
// ============================================================================
export interface ITaskActivityService extends IService {
    getTaskActivity(taskId: string): Promise<ServiceResponse<TaskActivity[]>>;
    logTaskActivity(params: LogTaskActivityParams): Promise<ServiceResponse<void>>;
    exportActivities(params: ExportActivitiesParams): Promise<ServiceResponse<ExportActivitiesResult>>;
}
export class TaskActivityService implements ITaskActivityService {
    readonly name = "TaskActivityService";
    async initialize(): Promise<void> {
        logger.info("✅ TaskActivityService initialized");
    }
    async dispose(): Promise<void> {
        logger.info("🧹 TaskActivityService disposed");
    }
    async getTaskActivity(taskId: string): Promise<ServiceResponse<TaskActivity[]>> {
        const startTime = Date.now();
        logger.debug("Fetching activity for task", { taskId });
        try {
            const { data, error } = await typedSupabase.rpc("get_task_activity_with_users", {
                p_task_id: taskId,
            });
            if (error) {
                return { success: false, error: error.message, code: "FETCH_FAILED", service: this.name, context: { taskId, error } };
            }
            return { success: true, data: (data as TaskActivity[]) || [] };
        }
        catch (error) {
            const serviceError = error instanceof Error ? error : new Error("Unknown error");
            return { success: false, error: serviceError.message, code: "FETCH_FAILED", service: this.name, context: { taskId, error } };
        }
    }
    async logTaskActivity(params: LogTaskActivityParams): Promise<ServiceResponse<void>> {
        const startTime = Date.now();
        try {
            const { data: { user }, } = await typedSupabase.auth.getUser();
            if (!user) {
                return { success: false, error: "User not authenticated", code: "AUTH_REQUIRED", service: this.name };
            }
            // Log the activity to database
            const { error } = await typedSupabase.rpc("log_task_activity", {
                p_task_id: params.taskId,
                p_user_id: user.id,
                p_action_type: params.actionType,
                p_field_name: params.fieldName ?? undefined,
                p_old_value: params.oldValue ?? undefined,
                p_new_value: params.newValue ?? undefined,
                p_details: params.details
                    ? JSON.parse(JSON.stringify(params.details))
                    : undefined,
            });
            if (error) {
                // Don't throw error for logging failures, just return error response
                logger.error("Error logging task activity", { error, params });
                return { success: false, error: error.message, code: "LOG_FAILED", service: this.name, context: { params, error } };
            }
            // Automatically create notification from activity (unless explicitly skipped)
            if (!params.skipNotification) {
                // Fire and forget - don't wait for notification creation
                activityNotificationBridge.processActivity({
                    sourceType: "task",
                    sourceId: params.taskId,
                    actionType: params.actionType,
                    userId: user.id,
                    actorId: user.id,
                    targetUserId: params.targetUserId,
                    details: {
                        ...params.details,
                        fieldName: params.fieldName,
                        oldValue: params.oldValue,
                        newValue: params.newValue,
                    },
                }).catch((error) => {
                    logger.warn("Failed to create notification from activity", {
                        error,
                        actionType: params.actionType,
                        taskId: params.taskId,
                    });
                });
            }
            return { success: true, data: undefined };
        }
        catch (error) {
            const serviceError = error instanceof Error ? error : new Error("Unknown error");
            // Don't throw error for logging failures
            logger.error("Error logging task activity", { error: serviceError, params });
            return { success: false, error: serviceError.message, code: "LOG_FAILED", service: this.name, context: { params, error } };
        }
    }
    async exportActivities(params: ExportActivitiesParams): Promise<ServiceResponse<ExportActivitiesResult>> {
        const startTime = Date.now();
        logger.debug("Exporting task activities", { params });
        try {
            const { data: { user }, } = await typedSupabase.auth.getUser();
            if (!user) {
                return { success: false, error: "User not authenticated", code: "AUTH_REQUIRED", service: this.name };
            }
            interface ActivityQueryResult {
                id: string;
                task_id: string;
                user_id: string;
                action_type: string;
                field_name: string | null;
                old_value: string | null;
                new_value: string | null;
                details: Record<string, unknown> | null;
                created_at: string;
                profiles: {
                    username: string | null;
                    full_name: string | null;
                    avatar_url: string | null;
                } | null;
            }
            // Build query
            let query = typedSupabase
                .from("task_activity")
                .select(`
          *,
          profiles:user_id (
            username,
            full_name,
            avatar_url
          )
        `)
                .order("created_at", { ascending: false });
            // Apply filters
            if (params.taskIds && params.taskIds.length > 0) {
                query = query.in("task_id", params.taskIds);
            }
            if (params.startDate) {
                query = query.gte("created_at", params.startDate);
            }
            if (params.endDate) {
                query = query.lte("created_at", params.endDate);
            }
            if (params.actionTypes && params.actionTypes.length > 0) {
                query = query.in("action_type", params.actionTypes);
            }
            if (params.userIds && params.userIds.length > 0) {
                query = query.in("user_id", params.userIds);
            }
            const { data: activities, error } = await query;
            if (error) {
                return { success: false, error: error.message, code: "EXPORT_FAILED", service: this.name, context: { params, error } };
            }
            if (!activities || activities.length === 0) {
                return { success: false, error: "No activities found matching the criteria", code: "NO_DATA", service: this.name, context: { params } };
            }
            // Format data based on requested format
            if (params.format === "json") {
                const exportData = {
                    exported_at: new Date().toISOString(),
                    exported_by: user.id,
                    filter_params: params,
                    total_count: activities.length,
                    activities: (activities as unknown as ActivityQueryResult[]).map((activity) => ({
                        id: activity.id,
                        task_id: activity.task_id,
                        user_id: activity.user_id,
                        username: activity.profiles?.username ?? undefined,
                        full_name: activity.profiles?.full_name ?? undefined,
                        action_type: activity.action_type,
                        field_name: activity.field_name ?? undefined,
                        old_value: activity.old_value ?? undefined,
                        new_value: activity.new_value ?? undefined,
                        details: activity.details ?? undefined,
                        created_at: activity.created_at,
                    })),
                };
                return { success: true, data: {
                        data: JSON.stringify(exportData, null, 2),
                        filename: `task_activities_${new Date().toISOString().split("T")[0]}.json`,
                        mimeType: "application/json",
                    } };
            }
            else {
                // CSV format
                const headers = [
                    "ID",
                    "Task ID",
                    "User ID",
                    "Username",
                    "Full Name",
                    "Action Type",
                    "Field Name",
                    "Old Value",
                    "New Value",
                    "Created At",
                ];
                const escapeCsvValue = (value: unknown): string => {
                    if (value === null || value === undefined)
                        return "";
                    const stringValue = String(value);
                    if (stringValue.includes(",") || stringValue.includes('"') || stringValue.includes("\n")) {
                        return `"${stringValue.replace(/"/g, '""')}"`;
                    }
                    return stringValue;
                };
                const rows = (activities as unknown as ActivityQueryResult[]).map((activity) => [
                    escapeCsvValue(activity.id),
                    escapeCsvValue(activity.task_id),
                    escapeCsvValue(activity.user_id),
                    escapeCsvValue(activity.profiles?.username),
                    escapeCsvValue(activity.profiles?.full_name),
                    escapeCsvValue(activity.action_type),
                    escapeCsvValue(activity.field_name),
                    escapeCsvValue(activity.old_value),
                    escapeCsvValue(activity.new_value),
                    escapeCsvValue(activity.created_at),
                ]);
                const csvData = [headers.join(","), ...rows.map((row) => row.join(","))].join("\n");
                return { success: true, data: {
                        data: csvData,
                        filename: `task_activities_${new Date().toISOString().split("T")[0]}.csv`,
                        mimeType: "text/csv",
                    } };
            }
        }
        catch (error) {
            const serviceError = error instanceof Error ? error : new Error("Unknown error");
            logger.error("Error exporting task activities", { error: serviceError, params });
            return { success: false, error: serviceError.message, code: "EXPORT_FAILED", service: this.name, context: { params, error } };
        }
    }
}
// ============================================================================
// SINGLETON EXPORT - Following architectural consolidation plan
// ============================================================================
export const taskActivityService = new TaskActivityService();
